import { useState, memo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { trans } from '@spotahome/soyuz/client';
import SoyuzAnalytics from '@spotahome/soyuz-analytics';

import { FormDate, Checkbox } from '@spotahome/ui-library';
import {
  isDateDisabledFactory,
  getMinAvailableDate
} from '@spotahome/ui-library/src/tenant/components/utils/listing-dates-validator-service';

import datesRangeShape from '../../shapes/datesRange';

import DateRangePicker from './DateRangePicker';

import './BookNowDatesInput.scss';

const sendToggleEvent = action => () => {
  SoyuzAnalytics.sendGA4Event('send', {
    context: 'book-now',
    section: 'date-picker',
    action: `${action}`
  });
};

const BookNowDatesInput = ({
  className = '',
  minStay = '',
  maxStay = '',
  moveIn = '',
  moveOut = '',
  onChange,
  show = false,
  availabilities = [],
  initiallyOpen = false,
  moveInDatesRanges = [],
  onCalendarClose = () => {},
  contractDurationTypesAllowed = ['temporal'],
  absolutePosition = true,
  cityId
}) => {
  const [showMoveOut, setShowMoveOut] = useState(!!moveOut);

  const datesI18n = {
    dateInPlaceholder: trans('property_show.book_now_card.placeholder'),
    dateOutPlaceholder: trans('property_show.book_now_card.placeholder'),
    dateInLabel: trans('property_show.book_now_card.date_in'),
    dateOutLabel: trans('property_show.book_now_card.date_out')
  };
  const minDate = getMinAvailableDate(moveInDatesRanges);

  const disableDayFnMoveIn = date =>
    isDateDisabledFactory(moveInDatesRanges)(date);

  const disableDayFnMoveOut = date =>
    isDateDisabledFactory(availabilities)(date);

  const isTemporalAllowed = contractDurationTypesAllowed.includes('temporal');
  const isPermanentAllowed = contractDurationTypesAllowed.includes('permanent');
  const durationsAllowed =
    (isTemporalAllowed && isPermanentAllowed && 'mixed') ||
    (isPermanentAllowed && 'permanent') ||
    'temporal';

  const handleShowMoveOutChange = value => {
    setShowMoveOut(value);
    onChange({
      moveIn: moveIn || null,
      moveOut: value ? moveOut : null
    });
  };

  const shouldRenderFromTo =
    durationsAllowed === 'temporal' ||
    (durationsAllowed === 'mixed' && showMoveOut);

  const handleOnCalendarClose = () => {
    sendToggleEvent('close')();
    onCalendarClose();
  };

  return (
    <div className={classNames('book-now-dates-input', className)}>
      <div className="book-now-dates-input__date-pickers">
        {shouldRenderFromTo && (
          <DateRangePicker
            minDate={minDate}
            moveIn={moveIn}
            minStay={minStay}
            maxStay={maxStay}
            onChange={onChange}
            moveOut={moveOut}
            disableDayFnMoveIn={disableDayFnMoveIn}
            disableDayFnMoveOut={disableDayFnMoveOut}
            cityId={cityId}
            onOpen={sendToggleEvent('open')}
            onClose={handleOnCalendarClose}
            show={show}
            initiallyOpen={initiallyOpen}
          />
        )}
        {(durationsAllowed === 'permanent' ||
          (durationsAllowed === 'mixed' && !showMoveOut)) && (
          <FormDate
            bold
            calendarBordered
            absolutePosition={absolutePosition}
            isCalendarOpen={show && !moveIn}
            value={moveIn}
            minDate={minDate}
            title={datesI18n.dateInLabel}
            calendarPosition={moveIn || minDate}
            initiallyOpen={initiallyOpen}
            placeholder={datesI18n.dateInPlaceholder}
            onChange={({ value }) => onChange({ moveIn: value, moveOut: null })}
            onCalendarClose={onCalendarClose}
            disableDayFn={disableDayFnMoveIn}
            dataTest="form-date-single"
            dataTestCalendar="form-date-single-calendar"
          />
        )}
      </div>
      {durationsAllowed === 'mixed' && (
        <div className="book-now-dates-input__checkbox">
          <Checkbox
            id="move-out-checkbox"
            checked={showMoveOut}
            callback={handleShowMoveOutChange}
          />
          <label htmlFor="move-out-checkbox">
            {trans('property_show.book_now_card.long-term-checkbox')}
          </label>
        </div>
      )}
    </div>
  );
};

BookNowDatesInput.propTypes = {
  className: PropTypes.string,
  availabilities: PropTypes.arrayOf(PropTypes.shape(datesRangeShape)),
  moveIn: PropTypes.string,
  moveOut: PropTypes.string,
  moveInDatesRanges: PropTypes.arrayOf(PropTypes.shape(datesRangeShape)),
  contractDurationTypesAllowed: PropTypes.arrayOf(
    PropTypes.oneOf(['temporal', 'permanent'])
  ),
  onChange: PropTypes.func.isRequired,
  minStay: PropTypes.number,
  maxStay: PropTypes.number,
  show: PropTypes.bool,
  initiallyOpen: PropTypes.bool,
  onCalendarClose: PropTypes.func,
  absolutePosition: PropTypes.bool,
  cityId: PropTypes.string.isRequired
};

function canRemainWithoutRerender(prevProps, nextProps) {
  return (
    prevProps.moveIn === nextProps.moveIn &&
    prevProps.moveOut === nextProps.moveOut &&
    prevProps.show === nextProps.show &&
    prevProps.initiallyOpen === nextProps.initiallyOpen
  );
}

export default memo(BookNowDatesInput, canRemainWithoutRerender);
